import { REVIEW_FIELDS } from "../TreatmentPlans/lib/policyCompleteness";
import { choicePayload, UNKNOWN_CARRIER_CODE } from "./carrierChoice";

/**
 * Policies we could NOT save yet — from the PMS, or read off a claim form.
 *
 * `InsurancePolicy.insurance_provider` is a non-nullable PROTECT FK, so a policy
 * whose carrier we can't match to our list cannot exist as a row at all — it has
 * to stay client-side until someone picks the carrier. These helpers give such a
 * policy just enough shape to travel through the wizard as an ordinary card, and
 * to be turned into a real row once the carrier is known.
 *
 * The wizard reviews and approves every policy in its Details step anyway, so
 * that is where the carrier question belongs — not in a separate modal on top of
 * the panel asking the same kind of question in a different shape.
 */

const PENDING_PREFIX = "pending:";
// A claim-form plan in the same slot as a PMS one is a different card.
export const CLAIM_FORM_PENDING_PREFIX = PENDING_PREFIX + "claim_form:";

export function isPendingId(id) {
  return typeof id === "string" && id.indexOf(PENDING_PREFIX) === 0;
}

/** Where a pending card came from: "claim_form" or "pms". */
export function pendingSource(card) {
  const preview = card && card.__preview;
  return preview && preview.source === "claim_form" ? "claim_form" : "pms";
}

/**
 * A claim-form plan's values, with the blanks our records could fill
 * (`on_file` from claim-form/policies/ — the member's name and ID, which a CDA
 * form's second plan never prints). The card shows them and approving saves
 * them, like anything else the card carries.
 */
export function claimFormValues(preview) {
  const values = Object.assign({}, (preview && preview.values) || {});
  const onFile = (preview && preview.on_file && preview.on_file.values) || {};
  Object.keys(onFile).forEach(function (field) {
    if (onFile[field] && !String(values[field] || "").trim()) values[field] = onFile[field];
  });
  return values;
}

/** The pill on a pending card — it isn't a policy yet, and this says why. */
export function pendingLabel(card) {
  return pendingSource(card) === "claim_form"
    ? "From the claim form · not saved yet"
    : "From your PMS · not saved yet";
}

/**
 * Preview payloads -> cards `PolicySelectStep` and `PolicyReview` can
 * render without knowing any of the above.
 *
 * The reviewed fields are lifted out of the preview's values under their real names so
 * `missingCriticalFields()` works on a pending card exactly as it does on a saved
 * policy — the "Needs member ID" hint, the completeness gate and the Details form
 * all keep working with no special cases.
 */
export function toPendingCards(pendingReview) {
  const policies = (pendingReview && pendingReview.policies) || [];
  return policies.map(function (preview) {
    const carrier = preview.carrier || {};
    const fromForm = preview.source === "claim_form";
    // A claim-form preview carries `values` and `name_raw`; a PMS one the
    // `sikka_*` keys it has always had.
    const values = fromForm ? claimFormValues(preview) : preview.values || preview.sikka_values || {};
    const card = {
      id: (fromForm ? CLAIM_FORM_PENDING_PREFIX : PENDING_PREFIX) + preview.insurance_type,
      // What the PMS (or the form) calls this carrier. It is all we have to show
      // until someone tells us which of ours it is, and it is what makes the
      // choice possible.
      insurance_name_display:
        carrier.name_raw || carrier.sikka_carrier_name_raw || "Unrecognised carrier",
      insurance_type: preview.insurance_type,
      insurance_type_display: preview.insurance_type,
      // Carried so the checklist can offer candidates and build the save payload.
      __preview: preview,
    };
    // REVIEW_FIELDS, not just the critical five: the relation is saved with the
    // card too, so the card has to show it rather than an empty select.
    REVIEW_FIELDS.forEach(function (field) {
      card[field] = values[field] || "";
    });
    return card;
  });
}

/**
 * One entry for `POST patient/insurance/save-from-sikka-preview/`.
 *
 * Mirrors what SikkaPolicyReviewModal builds (see its `policiesPayload`): take
 * every field the PMS offered that the user hasn't locked, and send their edits
 * as overrides. Employer is opt-in there via `fields_to_write`; it is included
 * here whenever the PMS returned one, since this flow has no separate toggle for
 * it and dropping it would silently lose data the modal would have kept.
 */
export function buildPreviewSavePayload(preview, carrier, draft) {
  const fieldStates = preview.field_states || {};
  const fieldsToWrite = Object.keys(fieldStates).filter(function (field) {
    // A manually edited field is a human decision; never overwrite it from the PMS.
    return fieldStates[field] !== "manually_edited";
  });
  if (preview.employer && preview.employer.name) fieldsToWrite.push("employer_name");

  const overrides = {};
  Object.keys(draft || {}).forEach(function (field) {
    const value = draft[field];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      overrides[field] = value;
    }
  });

  // `carrier` is a CarrierResolution choice ({kind: "provider"|"create", …}),
  // or — from older callers — a bare provider id.
  const carrierChoice = carrier && typeof carrier === "object"
    ? choicePayload(carrier)
    : { insurance_provider_id: carrier === undefined ? null : carrier };

  return {
    insurance_type: preview.insurance_type,
    carrier_choice: carrierChoice,
    fields_to_write: fieldsToWrite,
    field_overrides: overrides,
  };
}

/**
 * Carrier options for the Details step, best guesses first.
 *
 * The preview's ranked candidates lead — they are the whole reason a human is
 * being asked — with their match score shown so a weak suggestion looks weak.
 * The full provider list follows so anything can still be chosen.
 */
// The backend's placeholder carrier row ("Unknown carrier") — defined with the
// rest of the carrier logic. The list endpoint already hides it from non-admins;
// this keeps a super-admin from picking it by accident too.
export { UNKNOWN_CARRIER_CODE };

function isSelectableCarrier(p) {
  return p && p.code !== UNKNOWN_CARRIER_CODE;
}

export function carrierOptions(preview, providers) {
  const candidates = ((preview && preview.carrier && preview.carrier.candidates) || []).filter(isSelectableCarrier);
  const seen = {};
  const options = [];

  candidates.forEach(function (c) {
    seen[c.id] = true;
    options.push({
      value: c.id,
      label: c.name || c.code,
      score: c.score,
    });
  });

  (providers || []).forEach(function (p) {
    if (seen[p.id] || !isSelectableCarrier(p)) return;
    options.push({ value: p.id, label: p.name || p.code });
  });

  return options;
}
